import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Col, Dropdown, Layout, Row } from 'antd';
import Cookies from 'js-cookie';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import './styles.scss';
import * as routePath from 'routers/route-path';
import * as routeName from 'routers/route-name';
import { TOKEN } from 'constants/cookies';
import { USER_LEVEL_TYPE, USER_ROLE_TYPES } from 'constants/common';
import Icon, {
  AccountIcon,
  AvatarAddIcon,
  AvatarIcon,
  ProfileIcon,
  SupportIcon,
  CustomerIcon,
  UserIcon,
  WalletIcon,
  EditIcon,
  DashboardIcon,
  SignOutIcon,
} from 'components/Atoms/Icon';
import { showModalConfirmStylePrimary } from 'utilities/modal';
import { getFullName, getInitials } from 'utilities/stringHelper';
import { getLogoComponent, isMobile } from 'utilities/common';
import useProfile, { multipleAccount } from 'hooks/useProfile';
import CartBadge from './components/CartBadge';
import SearchCampaign from './components/SearchCampaign';
import ModalRequestFund from 'components/Pages/Wallet/components/WalletTransaction/components/ModalRequestFund';
import { MyButton, Language } from 'components/Atoms';
import ModalPoliticalAdvertiser from 'components/Pages/StartedPage/components/ModalPoliticalAdvertiser';
import { useLogout } from 'modules/auth/Login/services';
import { TierProgressBar } from 'components/AgencyPartner';
import usePartnerProgram, {
  useUpdateShowCongratulation,
} from 'hooks/usePartnerProgram';

import ModalCongratulation from 'modules/auth/Profile/components/ModalCongratulation';
import { getPartnerTier } from 'utilities/agencyPartnerHelper';
import { ACCOUNT_ROLE_ON_URL } from 'constants/url';

import { lowerCase, snakeCase } from 'lodash';
import { useBranding } from 'hooks/useBranding';
import useActions from 'hooks/useActions';
import { changeLanguage } from 'states/common/actions';

const { Header } = Layout;

const SiteHeader = () => {
  const history = useHistory();
  const { domainConfig, branding, getAgencyLogo, getTierName } = useBranding();
  const location = useLocation();
  const shouldHideLanguageSelect = domainConfig.languages.length <= 1;

  const [visibleRequestFunds, setVisibleRequestFunds] = useState(false);
  const [forceUserMenuUpdate, setForceUserMenuUpdate] = useState({});
  const [shouldRenderPoliticalDisclaimer, setShouldRenderPoliticalDisclaimer] =
    useState(false);
  const [showCongratulationModal, setShowCongratulationModal] = useState(false);

  const { data: userInfo, remove } = useProfile();
  const isAgencyPartnerProgram =
    userInfo?.role === USER_ROLE_TYPES.AGENCY && userInfo?.partnerProgram;

  const [changeLanguageAction] = useActions([changeLanguage]);

  const { data: partnerTierInfo } = usePartnerProgram({
    enabled: isAgencyPartnerProgram,
  });

  const tierName = getTierName(partnerTierInfo?.partnerProgram?.partnerTier);

  const updateShowCongratulation = useUpdateShowCongratulation();

  const logout = useLogout();

  const LogoComponent = getLogoComponent(branding?.logoFull);

  const homeLink =
    userInfo?.level !== USER_LEVEL_TYPE.ANALYTIC_MANAGER
      ? '/'
      : routePath.ANALYTICS.replace(
          ':accountRole',
          ACCOUNT_ROLE_ON_URL[userInfo.role]
        );

  useEffect(() => {
    if (userInfo) {
      let newTimezoneOffset = -userInfo.timeZone * 60;
      window.timezoneOffset = -newTimezoneOffset;

      changeLanguageAction(userInfo.languageCode);
      localStorage.setItem('language', userInfo.languageCode);
    }
  }, [userInfo]);

  useEffect(() => {
    if (partnerTierInfo?.isShowCongratulation) {
      setShowCongratulationModal(true);
    }
  }, [partnerTierInfo]);

  const handleCloseConfigurationModal = () => {
    updateShowCongratulation.mutate({
      id: partnerTierInfo.id,
    });
    setShowCongratulationModal(false);
  };

  const onForceUserMenuUpdate = useCallback(() => {
    setForceUserMenuUpdate({});
  }, []);

  const onToggleRequestFunds = useCallback(
    (value) => () => {
      setVisibleRequestFunds(value);
    },
    []
  );

  const handleLogout = (cb) => {
    const token = Cookies.get(TOKEN);

    showModalConfirmStylePrimary({
      title: 'header.sign_out',
      content: isMobile
        ? 'header.do_you_sign_out'
        : 'header.do_you_sign_out_all',
      cancelText: 'common.no',
      okText: 'common.yes',
      onOk: () => {
        if (cb) {
          cb();
        }
        return new Promise((resolve, reject) => {
          logout.mutate(
            {
              token: token,
              onCallback: () => {
                remove();
                Cookies.remove(TOKEN);
                Cookies.remove(TOKEN, {
                  domain: `.${domainConfig.hostname}`,
                });
                history.push(routePath.LOGIN);
                resolve();
              },
            },
            {
              onError: () => {
                reject();
              },
            }
          );
        });
      },
    });
  };

  const userDropdown = useMemo(() => {
    const accounts = multipleAccount
      .getAccounts()
      .filter((acc) => acc.id !== userInfo?.id);

    return (
      <div className="user-dropdown">
        {isAgencyPartnerProgram && partnerTierInfo !== undefined ? (
          <div className="main-profile">
            <div className="d-flex">
              <div className="avatar-wrapper bg-color-unset">
                <img
                  src={getAgencyLogo(
                    partnerTierInfo?.partnerProgram?.partnerTier
                  )}
                />
              </div>
              <div>
                <div className="name mt-15 fw-600 fz-16 text-1-line word-break">
                  <FormattedMessage
                    id={`header.${snakeCase(domainConfig.name)}`}
                    defaultMessage={domainConfig.name}
                  />
                </div>
                <div
                  className={`tier-text fw-600 mt-4 text-placeholder text-1-line word-break partner-text partner-text--${getPartnerTier(
                    partnerTierInfo
                  )}`}
                >
                  <FormattedMessage
                    id={`modal_tier_benefit.${lowerCase(tierName)}`}
                  />{' '}
                  <FormattedMessage id="header.tier_partner" />
                </div>
              </div>
            </div>
            <TierProgressBar
              partnerTierInfo={partnerTierInfo}
              className={'tier-progress--reverse'}
            />
          </div>
        ) : (
          <div className="main-profile">
            <div className="d-flex">
              <div className="avatar-wrapper">
                <AvatarIcon />
              </div>
              <div>
                <div className="name mt-15 fw-600 fz-16 text-1-line word-break">
                  {getFullName(userInfo?.firstName, userInfo?.lastName)}
                </div>
                <div className="mt-3 text-placeholder text-1-line word-break">
                  {userInfo?.email}
                </div>
                <div className="mt-3 text-placeholder text-1-line word-break w-200">
                  <FormattedMessage id="header.organization" />:{' '}
                  {userInfo?.companyName}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isMobile && (
          <div className="accounts-wrapper">
            {isAgencyPartnerProgram && (
              <div className="add-account-wrapper account-item">
                <Row
                  gutter={[10]}
                  wrap={false}
                  align="middle"
                  onClick={() => history.push(routePath.PROFILE)}
                >
                  <Col>
                    <div className="avatar-wrapper">
                      <DashboardIcon />
                    </div>
                  </Col>
                  <Col className="name">
                    <FormattedMessage id="header.view_partner_dashboard" />
                  </Col>
                </Row>
              </div>
            )}
            {accounts.map((acc) => (
              <div key={acc.id} className="account-item">
                <Row
                  gutter={[10]}
                  wrap={false}
                  align="middle"
                  onClick={() => {
                    Cookies.set(TOKEN, acc.token);
                    Cookies.set(TOKEN, acc.token, {
                      domain: `.${domainConfig.hostname}`,
                    });
                    window.location.href = '/';
                  }}
                >
                  <Col>
                    <div className="avatar-wrapper">
                      <AvatarIcon />
                    </div>
                  </Col>
                  <Col flex="auto">
                    <div className="name fw-600 text-1-line word-break">
                      {acc.name}
                    </div>
                    <div className="email text-placeholder mt-1 text-1-line word-break">
                      {acc.email}
                    </div>
                  </Col>
                  <Col className="sign-out">
                    <MyButton
                      className="pr-0"
                      type="link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        multipleAccount.signOutAccount(acc.id);
                        onForceUserMenuUpdate();
                      }}
                    >
                      <FormattedMessage id="header.sign_out" />
                    </MyButton>
                  </Col>
                </Row>
              </div>
            ))}
            {!isAgencyPartnerProgram && (
              <div className="add-account-wrapper account-item">
                <Row
                  gutter={[10]}
                  wrap={false}
                  align="middle"
                  onClick={() => history.push(routePath.PROFILE)}
                >
                  <Col>
                    <div className="avatar-wrapper">
                      <Icon component={ProfileIcon} />
                    </div>
                  </Col>
                  <Col className="name">
                    <FormattedMessage id="header.update_profile" />
                  </Col>
                </Row>
              </div>
            )}

            <div className="add-account-wrapper account-item">
              <Row
                gutter={[10]}
                wrap={false}
                align="middle"
                onClick={() => history.push(routePath.TEAM)}
              >
                <Col>
                  <div className="avatar-wrapper">
                    <Icon component={CustomerIcon} />
                  </div>
                </Col>
                <Col className="name">
                  <FormattedMessage id="header.team_management" />
                </Col>
              </Row>
            </div>

            {userInfo?.level === USER_LEVEL_TYPE.CAMPAIGN_MANAGER &&
              !userInfo?.manageCard && (
                <div className="add-account-wrapper account-item">
                  <Row
                    gutter={[10]}
                    wrap={false}
                    align="middle"
                    onClick={onToggleRequestFunds(true)}
                  >
                    <Col>
                      <div className="avatar-wrapper">
                        <Icon component={WalletIcon} />
                      </div>
                    </Col>
                    <Col className="name">
                      <FormattedMessage id="header.request_funds" />
                    </Col>
                  </Row>
                </div>
              )}
            {userInfo?.role === USER_ROLE_TYPES.CAMPAIGN &&
              userInfo?.level !== USER_LEVEL_TYPE.ANALYTIC_MANAGER && (
                <div className="add-account-wrapper account-item">
                  <Row
                    gutter={[10]}
                    wrap={false}
                    align="middle"
                    onClick={() => setShouldRenderPoliticalDisclaimer(true)}
                  >
                    <Col>
                      <div className="avatar-wrapper">
                        <Icon component={EditIcon} />
                      </div>
                    </Col>
                    <Col className="name">
                      {routeName.EDIT_POLITICAL_DISCLAIMER}
                    </Col>
                  </Row>
                </div>
              )}
            <div className="add-account-wrapper account-item">
              <Row
                gutter={[10]}
                wrap={false}
                align="middle"
                onClick={() => history.push(routePath.LOGIN)}
              >
                <Col>
                  <div className="avatar-wrapper">
                    <AvatarAddIcon />
                  </div>
                </Col>
                <Col className="name">
                  <FormattedMessage id="header.add_another_account" />
                </Col>
              </Row>
            </div>
          </div>
        )}
        <div className="accounts-wrapper">
          <div className="add-account-wrapper account-item">
            <Row
              gutter={[10]}
              wrap={false}
              align="middle"
              onClick={() => {
                handleLogout(() => multipleAccount.signOutAllAccount());
              }}
            >
              <Col>
                <div className="avatar-wrapper">
                  <SignOutIcon />
                </div>
              </Col>
              <Col className="name">
                {isMobile ? (
                  <FormattedMessage id="header.sign_out" />
                ) : (
                  <FormattedMessage id="header.sign_out_all" />
                )}
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }, [userInfo, forceUserMenuUpdate, partnerTierInfo]);

  const userBlock = useMemo(() => {
    return (
      <Dropdown overlay={userDropdown} overlayClassName="user-block-overlay">
        {isMobile ? (
          <AccountIcon className="account-icon" />
        ) : (
          <Avatar className="user-avatar">
            {getInitials(userInfo?.firstName, userInfo?.lastName)}
          </Avatar>
        )}
      </Dropdown>
    );
  }, [userInfo, forceUserMenuUpdate, partnerTierInfo]);

  return (
    <Header id="site-header">
      <Row align="middle" gutter={[24]} style={{ height: '100%' }}>
        <Col span={2}>
          {isMobile && (
            <Link to={homeLink} className="logo-wrapper">
              <LogoComponent />
            </Link>
          )}
        </Col>
        <Col flex="auto" />

        <Col span={16} className="d-flex justify-content-end">
          {!shouldHideLanguageSelect && <Language />}
          <div className="mr-16 d-flex align-items-center">
            {!isMobile ? (
              <div className="flex-center align-items-center">
                {location?.pathname?.includes(routePath.STORE_TEMPLATE) && (
                  <CartBadge />
                )}

                <Link to={routePath.SUPPORT} className="link-icon">
                  <div className="flex-center align-items-center">
                    <SupportIcon title={routeName.SUPPORT} />
                  </div>
                </Link>
              </div>
            ) : (
              <SearchCampaign />
            )}
          </div>
          <div className="action d-flex align-items-center">{userBlock}</div>
        </Col>
      </Row>

      <ModalRequestFund
        visible={visibleRequestFunds}
        onClose={onToggleRequestFunds(false)}
      />
      {shouldRenderPoliticalDisclaimer && (
        <ModalPoliticalAdvertiser
          isOpened={shouldRenderPoliticalDisclaimer}
          setIsOpened={setShouldRenderPoliticalDisclaimer}
          isEdit={true}
        />
      )}
      {showCongratulationModal && (
        <ModalCongratulation
          partnerTierInfo={partnerTierInfo}
          partnerTierLogo={getAgencyLogo(
            partnerTierInfo?.partnerProgram?.partnerTier
          )}
          visible={showCongratulationModal}
          onClose={() => handleCloseConfigurationModal()}
        />
      )}
    </Header>
  );
};

export default SiteHeader;
